import { NavLink, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useEvent } from "@/context/EventContext";
import { HelpFab } from "@/components/HelpFab";
import { LayoutDashboard, Users, Armchair, Images, Settings, LogOut, Menu, X, Heart, Wallet, ListChecks, Briefcase, Gift, Mail, MailOpen, Sparkles, Lock, ChevronLeft } from "lucide-react";

const hostLinks = [
  { seg: "dashboard", label: "Overview", icon: LayoutDashboard },
  { seg: "guests", label: "Guests", icon: Users },
  { seg: "invitation", label: "Invitation", icon: MailOpen },
  { seg: "tasks", label: "Checklist", icon: ListChecks, feature: "tasks" },
  { seg: "vendors", label: "Vendors", icon: Briefcase, feature: "vendors" },
  { seg: "budget", label: "Budget", icon: Wallet, feature: "budget" },
  { seg: "seating", label: "Seating", icon: Armchair, feature: "seating" },
  { seg: "gallery", label: "Gallery", icon: Images, feature: "gallery" },
  { seg: "thankyou", label: "Thank You", icon: Gift, feature: "thankyou" },
  { seg: "messages", label: "Messages", icon: Mail },
  { seg: "setup", label: "Event", icon: Settings },
  { seg: "pricing", label: "Unlock Features", icon: Sparkles },
];
const attendeeLinks = [
  { seg: "dashboard", label: "Overview", icon: LayoutDashboard },
  { seg: "gallery", label: "Gallery", icon: Images },
];

export function EventLayout({ children }) {
  const { user, logout } = useAuth();
  const { eventId, event, features, role } = useEvent();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const links = role === "attendee" ? attendeeLinks : hostLinks;
  const isLocked = (f) => f && features && !(features.unlocked || []).includes(f);
  const handleLogout = () => { logout(); navigate("/login"); };

  const NavItems = ({ onClick }) => (
    <>
      {links.map((l) => (
        <NavLink key={l.seg} to={`/e/${eventId}/${l.seg}`} end onClick={onClick}
          data-testid={`nav-${l.seg}`}
          className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors duration-200 ${isActive ? "bg-[#8C9A86] text-white" : "text-stone-600 hover:bg-stone-100"}`}>
          <l.icon size={17} strokeWidth={1.6} />
          <span className="flex-1">{l.label}</span>
          {isLocked(l.feature) && <Lock size={12} className="text-[#c0a06a]" data-testid={`nav-lock-${l.feature}`} />}
          {l.seg === "pricing" && <Sparkles size={12} className="text-[#c0a06a]" />}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "#F5F4F0" }}>
      <aside className="hidden lg:flex flex-col w-64 border-r border-[#E8E6E1] bg-white/70 backdrop-blur-xl fixed h-screen">
        <div className="px-5 py-5 border-b border-[#E8E6E1]">
          <Link to="/" data-testid="brand-home" className="flex items-center gap-2 text-stone-800">
            <Heart size={16} className="text-[#8C9A86]" fill="#8C9A86" />
            <span className="font-serif text-lg tracking-tight">Evermore</span>
          </Link>
          <Link to="/app" data-testid="back-to-events" className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-700 mt-3"><ChevronLeft size={14} /> Back to my events</Link>
          <p className="font-serif text-base tracking-tight truncate mt-3 text-stone-800">{event?.title || "Event"}</p>
          <p className="overline mt-1">{event?.template?.name || "Event"}</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto"><NavItems /></nav>
        <div className="px-4 py-4 border-t border-[#E8E6E1]">
          <p className="text-sm font-medium text-stone-700 truncate">{user?.name}</p>
          <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center gap-2 text-sm text-stone-500 hover:text-[#C67A7A] transition-colors mt-2"><LogOut size={15} /> Sign out</button>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-16 bg-white/80 backdrop-blur-xl border-b border-[#E8E6E1]">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/" data-testid="brand-home-mobile" className="flex items-center gap-1.5"><Heart size={16} className="text-[#8C9A86]" fill="#8C9A86" /><span className="font-serif text-lg">Evermore</span></Link>
          <Link to="/app" data-testid="back-to-events-mobile" className="text-stone-400 hover:text-stone-700" title="Back to my events"><ChevronLeft size={18} /></Link>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="text-stone-700">{open ? <X size={22} /> : <Menu size={22} />}</button>
      </div>
      {open && (
        <div className="lg:hidden fixed top-16 inset-x-0 z-40 bg-white border-b border-[#E8E6E1] px-3 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
          <p className="px-4 pb-2 font-serif text-lg truncate">{event?.title || "Event"}</p>
          <NavItems onClick={() => setOpen(false)} />
          <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#C67A7A] w-full"><LogOut size={17} /> Sign out</button>
        </div>
      )}

      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 min-h-screen">{children}</main>
      <HelpFab />
    </div>
  );
}
